// Container page (Stage 6 plan §D "pages/WasteContainer.vue"): header facts and the actions the
// caller may see, as i18n keys and parameters. Pure: no Vue, no API calls.
import {
  formatDate,
  formatNumber,
  recordedText,
  weighingView,
} from "./wasteList.mjs";

export function headerView(detail, locale = "it") {
  const c = (detail && detail.container) || {};
  const waste = c.waste || {};
  return {
    id: c.id,
    name: c.name,
    code: waste.code,
    description: waste.description,
    hazardous: waste.hazardous === true,
    typeKey: `waste.type.${c.type}`,
    unitKey: `waste.unit.${c.unit}`,
    tare: c.tare == null ? null : formatNumber(c.tare, locale),
    note: c.note || null,
    inactive: c.status === "inactive",
    recorded: recordedText(c, locale),
    weighing: weighingView(c.latestWeighing, locale),
    // D6: the current weighing was taken before the last entry; a send-out needs a new one.
    weighingStale: Boolean(
      c.latestWeighing && c.latestWeighing.fresh === false,
    ),
  };
}

// Manager / Admin actions (canEdit comes from the API; it checks the role again). Anyone weighs,
// also an inactive container; a send-out is for a Manager / Admin.
export function actionsView(detail) {
  const canEdit = Boolean(detail && detail.canEdit);
  const inactive = Boolean(
    detail && detail.container && detail.container.status === "inactive",
  );
  return {
    edit: canEdit,
    deactivate: canEdit && !inactive,
    reactivate: canEdit && inactive,
    print: true,
    weigh: Boolean(detail && detail.container),
    sendOut: canEdit,
  };
}

// The PATCH set that flips the status.
export function statusSet(detail) {
  const inactive = detail.container.status === "inactive";
  return { status: inactive ? "active" : "inactive" };
}

// What the QR label prints: the plain id (D3), the name, the code, the last 6 characters.
export function labelData(container) {
  const id = String(container.id || "");
  return {
    id,
    name: container.name || "",
    code: (container.waste && container.waste.code) || "",
    shortId: id.slice(-6).toUpperCase(),
  };
}

// The "Ricevuto dalla bonifica" rows of the container page (Stage 6 slice 2): one per entry,
// newest first as the API sends them. reversal: the entry takes a quantity back out (a redo or a
// correction); reversed: a later entry took this one back.
export function entryRows(entries, locale = "it") {
  return ((entries && entries.items) || []).map((entry) => {
    const vehicle = entry.vehicle || {};
    const quantity = entry.quantity || null;
    return {
      id: entry.id,
      vehicle: vehicle.targa || vehicle.vin || "—",
      vehicleName: [vehicle.brand, vehicle.model].filter(Boolean).join(" "),
      dossierId: entry.dossierId || null,
      taskKey: entry.taskCode ? `workshop.task.${entry.taskCode}` : null,
      quantity: quantity
        ? `${entry.reversal ? "−" : ""}${formatNumber(quantity.value, locale)} ${quantity.unit}`
        : "—",
      basisKey:
        quantity && quantity.basis ? `workshop.basis.${quantity.basis}` : null,
      date: formatDate(entry.at || entry.recordedAt, locale),
      reversal: entry.reversal === true,
      reversed: entry.reversed === true,
    };
  });
}

// How many entries are not loaded yet ("Altri n").
export function moreEntries(entries, loaded) {
  const total = (entries && entries.total) || 0;
  return Math.max(0, total - (loaded || 0));
}

// An instant as day and time in Rome.
export function formatDateTime(value, locale = "it") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  }).format(date);
}

// The timeline of weighings and send-outs (Stage 6 slice 3), newest first as the API sends it.
// A weighing: its net kg, the method and its proof (scontrino or device), current or not. A
// send-out: kg, recipient, transporter, what the closed filling held and the document.
export function timelineRows(timeline, locale = "it") {
  return (timeline || []).map((item) => {
    const by = (item.by && item.by.name) || "";
    if (item.kind === "sendOut") {
      const content = item.content || {};
      let contentText;
      if (content.bodies) {
        contentText = {
          key: "waste.content.bodies",
          params: { count: content.bodies },
        };
      } else if (content.value) {
        contentText = {
          key: "waste.timeline.content",
          params: {
            value: formatNumber(content.value, locale),
            unit: content.unit,
          },
        };
      } else {
        contentText = { key: "waste.content.empty", params: {} };
      }
      return {
        id: item.id,
        kind: "sendOut",
        icon: "mdi-truck-outline",
        color: "primary",
        titleKey: "waste.timeline.send_out",
        params: {
          kg: formatNumber(item.weightKg, locale),
          recipient: item.recipient,
        },
        transporter: item.transporter || null,
        content: contentText,
        date: formatDate(item.date, locale),
        by,
        file: item.file
          ? { id: item.file.id, name: item.file.originalName || item.file.id }
          : null,
      };
    }
    return {
      id: item.id,
      kind: "weighing",
      icon: "mdi-scale",
      color: item.current ? "green" : "grey",
      titleKey: "waste.timeline.weighing",
      params: { kg: formatNumber(item.net, locale) },
      methodKey: `acceptance.weighings.methods_short.${item.method}`,
      proof:
        item.method === "gross_tare"
          ? {
              // The ticket number is optional for a container weighing.
              key: item.ticketNumber
                ? "waste.timeline.gross_tare"
                : "waste.timeline.gross_tare_no_ticket",
              params: {
                gross: formatNumber(item.gross, locale),
                tare: formatNumber(item.tare, locale),
                ticket: item.ticketNumber || "",
              },
            }
          : {
              key: "waste.timeline.device",
              params: { device: item.device || "" },
            },
      date: formatDateTime(item.weighedAt, locale),
      by,
      current: item.current === true,
      file: null,
    };
  });
}
